import { types, getParent, getRoot, flow, isAlive } from 'mobx-state-tree';
import { when } from 'mobx';
import Currency from '../../../models/SettingsStore/Currency';
import ConnectSource from './ConnectSource';
import { sellerWasConnectedOnOtherAccount } from '../../../constants/connectAccountErrors';
import { getPayoneerUrl, getRedirectStripeUrl } from '../../../utils/getEnvUrl';

const getBaseUrlAndTypeForAccountDelete = type => {
  const destinationType = type === 'destination';
  const mappedType = destinationType ? 'seller' : type;
  const baseUrl = destinationType ? 'seller-account' : 'account';
  return { mappedType, baseUrl };
};

const paypalAccount = types.model('paypalAccount', {
  id: types.string,
  name: types.string,
  isBillable: types.maybe(types.boolean),
});

const payoneerAccount = types.model('payoneerAccount', {
  id: types.string,
  name: types.string,
  isBillable: types.maybe(types.boolean),
});

const stripeAccount = types.model('stripeAccount', {
  email: types.string,
  cards: types.optional(types.array(types.frozen), []),
});

const ConnectionManagement = types
  .model('ConnectionManagement', {
    connectedAccounts: types.optional(types.array(Currency), []),
    disconnectedAccounts: types.optional(types.array(types.frozen), []),
    connectSource: types.optional(ConnectSource, {}),
    paypalBillingToken: types.optional(types.string, ''),
    paypalAccounts: types.optional(types.array(paypalAccount), []),
    payoneerAccounts: types.optional(types.array(payoneerAccount), []),
    stripeAccounts: types.optional(types.array(stripeAccount), []),
    latestAccount: types.optional(types.frozen, {}),
    lastConnectedPaypalName: '',
  })
  .views(self => ({
    get destinationAccountsById() {
      return Object.fromEntries(
        self.connectedAccounts[0]?.destination?.map(dest => [dest.id, dest]) ?? [],
      );
    },
    get transport5007() {
      return getParent(self).transport5007;
    },
    get accountCode() {
      return getRoot(self).uiStore.sessionStore.accountCode;
    },
    get userEmail() {
      return getRoot(self).uiStore.settingsStore.userProfile.email;
    },
    get saveWizardStep() {
      return getRoot(self).uiStore.onboardingStore.saveWizardState;
    },
    get redirectNextWizardStep() {
      return getRoot(self).uiStore.onboardingStore.setWizardStep;
    },
    get getDestinationTypes() {
      return getParent(self).markets.destinationTypes;
    },
    get destinationList() {
      return getParent(self).markets.destinations;
    },
    get showToast() {
      return getParent(self, 2).toastsStore.showToast;
    },
    get enums() {
      return getParent(self, 2).enumsStore.getEnumsList;
    },
    get billableAccounts() {
      return self.paypalAccounts.filter(({ isBillable }) => isBillable);
    },
    get billablePayoneerAccounts() {
      return self.payoneerAccounts.filter(({ isBillable }) => isBillable);
    },
    get billableStripeAccounts() {
      return self.stripeAccounts.filter(item => item.cards.find(card => card.isBillable === true));
    },
    get subscription() {
      return getParent(self).subscription;
    },
    get userProfile() {
      return getParent(self).userProfile;
    },
    get markets() {
      return getParent(self).markets;
    },
  }))
  .actions(self => ({
    fetchPaypalAccounts: flow(function* fetchPaypalAccounts() {
      try {
        const url = 'account/v1/paypal-accounts';
        const { data } = yield self.transport5007.fetch(url);
        if (isAlive(self)) {
          self.paypalAccounts = data;
        }
      } catch (err) {
        console.log(err);
      }
    }),
    fetchPayoneerAccounts: flow(function* fetchPayoneerAccounts() {
      try {
        const url = 'account/v1/payoneer-accounts';
        const { data } = yield self.transport5007.fetch(url);
        if (isAlive(self)) {
          self.payoneerAccounts = data;
        }
      } catch (err) {
        console.log(err);
      }
    }),
    fetchStripeAccounts: flow(function* fetchStripeAccounts() {
      try {
        const url = 'account/v1/stripe-accounts/grouped-by-email';
        const { data } = yield self.transport5007.fetch(url);
        if (isAlive(self)) {
          self.stripeAccounts = data;
        }
      } catch (err) {
        console.log(err);
      }
    }),
    deleteSellerAccount: flow(function* deleteSellerAccount(id, type, currency,marketplace) {
      try {
        const { baseUrl, mappedType } = getBaseUrlAndTypeForAccountDelete(type);
        let url = `${baseUrl}/v1/${mappedType}-accounts/${id}`;
        if (
          self.destinationAccountsById[id] &&
          self.destinationAccountsById[id].marketplaceTypeId === 'AMAZON'
        ) {
          url = `${baseUrl}/v1/${mappedType}-accounts/amazon/${id}`;
        }
        const headers = { 'Account-Id': self.accountCode };
        yield self.transport5007.delete(url, {}, {}, headers);
        if (type === 'paypal') {
          self.fetchPaypalAccounts();
        }
        if (type === 'payoneer') {
          self.fetchPayoneerAccounts();
        }
        if (type === 'stripe') {
          self.fetchStripeAccounts();
        }
        self.removeAccountFromList(id, type, currency,marketplace);
        self.showToast({
          type: self.enums.TOAST_TYPES.INFO,
          message: 'Account deleted successfully',
        });
      } catch (err) {
        console.error('deleting failed: ', err);
        self.showToast({
          type: self.enums.TOAST_TYPES.WARNING,
          message: 'There was an error deleting the account',
        });
      }
      self.markets.fetchSellersAccounts();
    }),
    removeAccountFromList(id, type, currencyId,marketplaceId) {
      try {
        const marketplaceIndex = self.connectedAccounts.findIndex(
          ({ marketplace }) => marketplace === marketplaceId,
        );
        if (marketplaceIndex !== -1) {
          self.connectedAccounts[marketplaceIndex][type] = self.connectedAccounts[marketplaceIndex][
            type
          ].filter(account => account.id !== id);
        }
      } catch (err) {
        console.log(err);
      }
    },
    fetchConnectedAccounts: flow(function* fetchConnectedAccounts() {
      try {
        const { data } = yield self.transport5007.fetch('account/v1/connections', {}, {});
        self.connectedAccounts = data;
      } catch (err) {
        console.log('fetching connected accounts failed: ', err);
      }
    }),
    fetchDisconnectedAccounts: flow(function* fetchDisconnectedAccounts() {
      try {
        const { data } = yield self.transport5007.fetch(
          'seller-account/v1/seller-accounts/disconnected',
          {},
          {},
        );
        self.disconnectedAccounts = data;
      } catch (err) {
        console.log('fetching disconnected accounts failed: ', err);
      }
    }),
    connectPaypalAccount: flow(function* createPaypalAccount(name, paymentMethodNonce = undefined) {
      try {
        yield self.transport5007.post('account/v1/paypal-accounts', {
          name,
          paymentMethodNonce,
        });
        self.lastConnectedPaypalName = name;
        self.showToast({
          type: self.enums.TOAST_TYPES.INFO,
          message: 'Account added successfully',
        });
        self.userProfile.updateOnboardingLevel('has paypal');
        self.saveWizardStep();
        // self.redirectNextWizardStep(3);
      } catch (err) {
        self.showToast({
          type: self.enums.TOAST_TYPES.WARNING,
          message: 'There was an error adding the account',
        });
      } finally {
        yield self.fetchConnectedAccounts();
        yield self.fetchPaypalAccounts();
      }
      // !self.billableAccounts.find(({id})=>self.subscription.billingPaypalAccountId === id)) &&
      console.log('ok');
      if (
        self.billableAccounts.length === 1 &&
        (!self.subscription.billingPaypalAccountId ||
          self.billableAccounts[0].id !== self.subscription.billingPaypalAccountId)
      ) {
        try {
          yield self.subscription.updateBillingPaypalAccount(self.billableAccounts[0].id);
        } catch (err2) {
          console.error(err2);
        }
      }
      return self.paypalAccounts.find(acc => acc.name === name);
    }),
    connectStripeAccount: flow(function* connectStripeAccount() {
      try {
        const { data } = yield self.transport5007.post('stripe-service/sessions ', {
          successUrl: getRedirectStripeUrl().success,
          cancelUrl: getRedirectStripeUrl().cancel,
          email: self.userEmail,
        });
        const y = window.top.outerHeight / 2 + window.top.screenY - 750 / 2;
        const x = window.top.outerWidth / 2 + window.top.screenX - 450 / 2;

        const stripeAuthWindow = window.open(
          data.url,
          'subscription',
          `toolbar=no, location=no, directories=no, status=no, menubar=no, scrollbars=no, resizable=no, copyhistory=no, width=450, height=750, top=${y}, left=${x}`,
        );

        window.addEventListener(
          'message',
          function handleWindowMessage(e) {
            if (!e.data.type || e.data.type !== 'OAuthAccessCode') {
              return;
            }
            window.removeEventListener('message', handleWindowMessage, false);
            if (e.data.client === '/successStripe') {
              self.setStripeSubmitResponse(e.data);
              stripeAuthWindow.close();
            } else {
              self.showToast({
                type: self.enums.TOAST_TYPES.WARNING,
                message: 'An error has occurred while adding the account. Please try again.',
              });
              stripeAuthWindow.close();
            }
          },
          false,
        );
      } catch (err) {
        console.error(err);
        self.showToast({
          type: self.enums.TOAST_TYPES.WARNING,
          message: 'An error has occurred while adding the account. Please try again.',
        });
      }
    }),
    setStripeSubmitResponse: flow(function* setStripeSubmitResponse(value) {
      try {
        yield self.transport5007.post('account/v1/stripe-accounts', {
          sessionId: value.payload,
        });
        if (self.subscription.stripeUpgradePlan.wantUpgrade) {
          yield self.subscription.upgradePlan(
            self.subscription.stripeUpgradePlan.planId,
            self.subscription.stripeUpgradePlan.poppedCardAddOns,
          );
          self.subscription.setWantUpgradeWithStripe(false);
          self.saveWizardStep();
          self.redirectNextWizardStep(3);
        }
        self.showToast({
          type: self.enums.TOAST_TYPES.INFO,
          message: 'You have successfully connected your credit card.',
        });
      } catch (err) {
        console.error(err);
        self.showToast({
          type: self.enums.TOAST_TYPES.WARNING,
          message: 'An error has occurred while adding your credit card. Please try again.',
        });
      } finally {
        yield self.fetchConnectedAccounts();
        yield self.fetchStripeAccounts();
      }
    }),
    connectPayoneerAccount: flow(function* connectPayoneerAccount() {
      try {
        self.payoneerSubmitResponse = {};
        const authWindow = window.open(getPayoneerUrl(), 'subscription', 'height=750, width=750');
        window.addEventListener(
          'message',
          function handleWindowMessage(e) {
            if (!e.data.type || e.data.type !== 'OAuthAccessCode') {
              return;
            }
            authWindow.close();
            window.removeEventListener('message', handleWindowMessage, false);
            if (e.data.payload.isOk) {
              self.showToast({
                type: self.enums.TOAST_TYPES.INFO,
                message: 'Account added successfully',
              });
              self.fetchConnectedAccounts();
              self.fetchPayoneerAccounts();
              getParent(self).subscription.fetchBillingPayoneerAccount();
              if (self.subscription.payoneerUpgradePlan.wantUpgrade) {
                self.subscription.upgradePlan(
                  self.subscription.payoneerUpgradePlan.planId,
                  self.subscription.payoneerUpgradePlan.poppedCardAddOns,
                );
                self.subscription.setWantUpgrade(false);
                self.saveWizardStep();
                self.redirectNextWizardStep(2);
              }
            } else {
              const errorMessage = Object.entries(e.data.payload)
                .map(([key, value]) => {
                  if (key !== 'isOk') return value;
                  return '';
                })
                .join('');

              console.warn(errorMessage);

              self.showToast({
                type: self.enums.TOAST_TYPES.WARNING,
                message: errorMessage,
              });
            }
          },
          false,
        );
        yield when(() => Object.keys(self.payoneerSubmitResponse).length !== 0);
        return true;
      } catch (err) {
        console.error(err);
        self.showToast({
          type: self.enums.TOAST_TYPES.WARNING,
          message: 'There was an error adding the account',
        });
        return false;
      }
    }),
    sendPayoneerAuthCode: flow(function* sendAuthCode(AuthorizationCode) {
      return yield self.transport5007.post(
        `account/v1/payoneer-accounts`,
        {
          AuthorizationCode,
        },
        {},
      );
    }),
    sendPayoneerCodeAfterConnection(code) {
      return new Promise((resolve, reject) => {
        when(
          () => getParent(self).markets.defaultDestination.id,
          () =>
            self
              .sendPayoneerAuthCode(code)
              .then(data => resolve(data))
              .catch(err => reject(err)),
        );
      });
    },
    fetchBillingToken: flow(function* fetchBillingToken() {
      try {
        const { data } = yield self.transport5007.fetch('paypal-api/v1/client', {}, {});
        self.paypalBillingToken = data.token;
      } catch (err) {
        console.error(err);
      }
    }),
    sendCodeAfterConnection(code, client) {
      return new Promise((resolve, reject) => {
        when(
          () => getParent(self).markets.defaultDestination.id,
          () =>
            self
              .sendAuthCode(code, client)
              .then(() => resolve())
              .catch(error => {
                if (
                  error.response.data &&
                  error.response.data.toString() === sellerWasConnectedOnOtherAccount
                )
                  self.showToast({
                    type: self.enums.TOAST_TYPES.WARNING,
                    message: error.response.data,
                  });
                reject(error);
              }),
        );
      });
    },
    sendAuthCode: flow(function* sendAuthCode(code, client) {
      const URL = 'seller-account/v1/seller-accounts';
      self.clearLatestAccount();
      const { data } = yield self.transport5007.post(
        URL,
        {
          destinationMarketplaceId: getParent(self).markets.defaultDestination.id,
          authorizationCode: code,
          developerCode: client,
          currency: getParent(self).markets.defaultCurrencyId,
        },
        {},
      );
      self.latestAccount = data;
    }),
    createNonApiAccount: flow(function* createNonApiAccount(storeName, paypalAddress, NonApiToken) {
      const URL = 'seller-account/v1/seller-accounts';
      self.clearLatestAccount();
      try {
        const { data } = yield self.transport5007.post(
          URL,
          {
            destinationMarketplaceId: getParent(self).markets.defaultDestination.id,
            authorizationCode: '',
            developerCode: '',
            storeName,
            paypalAddress,
            NonApiToken,
          },
          {},
        );
        self.latestAccount = data;
        return {
          success: true,
          message: '',
        };
      } catch (err) {
        return {
          success: false,
          message: err.response.data,
        };
      }
    }),
    transferListingsOrders: flow(function* transferListingsOrders(
      sourceAccountId,
      destinationAccountId = self.latestAccount?.id ?? '',
      destinationAccountName = self.latestAccount?.name ?? '',
    ) {
      const URL = 'seller-account/v1/seller-accounts/transfer';
      const data = {
        sourceAccountId,
        destinationAccountId,
        destinationAccountName,
      };
      try {
        yield self.transport5007.post(URL, data, {});
        self.showToast({
          type: self.enums.TOAST_TYPES.INFO,
          message: 'Listings and Orders transferred successfully',
        });
      } catch (err) {
        const {
          response: { data: errorMessage },
        } = err;
        self.showToast({
          type: self.enums.TOAST_TYPES.WARNING,
          message: errorMessage,
        });
        console.error('there was an error transferring data', errorMessage);
      }
    }),
    editPassword: flow(function* editPassword(id, name, newPassword) {
      try {
        const URL = `account/v1/source-accounts/${id}`;
        const data = {
          name,
          password: newPassword,
        };
        yield self.transport5007.put(URL, data, {}, {});
        self.showToast({
          type: self.enums.TOAST_TYPES.INFO,
          message: 'Password updated successfully',
        });
        return true;
      } catch (err) {
        console.error(err);
        self.showToast({
          type: self.enums.TOAST_TYPES.WARNING,
          message: 'Password update failed',
        });
        return false;
      }
    }),
    connectGlobalShipping: flow(function* connectGlobalShipping(id) {
      try {
        const URL = `seller-account/v1/seller-accounts/global-shipping/${id}`;
        const data = true;
        yield self.transport5007.post(URL, data, {
          headers: {
            'Content-Type': 'application/json',
          },
        });
        self.showToast({
          type: self.enums.TOAST_TYPES.INFO,
          message: 'Global shipping status updated successfully.',
        });
        return true;
      } catch (err) {
        console.error(err);
        self.showToast({
          type: self.enums.TOAST_TYPES.WARNING,
          message:
            'We encountered a problem while updating your global shipping status. Please try again.',
        });
        return false;
      }
    }),
    disconnectGlobalShipping: flow(function* disconnectGlobalShipping(id) {
      try {
        const URL = `seller-account/v1/seller-accounts/global-shipping/${id}`;
        const data = false;
        yield self.transport5007.post(URL, data, {
          headers: {
            'Content-Type': 'application/json',
          },
        });
        self.showToast({
          type: self.enums.TOAST_TYPES.INFO,
          message: 'Global shipping status updated successfully.',
        });
        return true;
      } catch (err) {
        console.error(err);
        self.showToast({
          type: self.enums.TOAST_TYPES.WARNING,
          message:
            'We encountered a problem while updating your global shipping status. Please try again.',
        });
        return false;
      }
    }),
    checkHasGlobalItemsInProcess: flow(function* checkHasGlobalItemsInProcess(id) {
      try {
        const headers = { 'Seller-Account-Id' : id }
        const URL = `seller-account/v1/seller-accounts/has-global-items-in-process`;
        const { data } = yield self.transport5007.fetch(URL, {} ,headers);
        return data;
      } catch (err) {
        console.error(err);
        return true;
      }
    }),
    clearLatestAccount() {
      self.latestAccount = {};
    },
    initConnections: flow(function* initConnections() {
      yield Promise.allSettled([
        self.fetchBillingToken(),
        self.fetchConnectedAccounts(),
        self.fetchPaypalAccounts(),
        self.fetchPayoneerAccounts(),
        self.fetchStripeAccounts(),
        self.fetchDisconnectedAccounts(),
        self.connectSource.fetchAccounts(getParent(self).markets.defaultCurrencyId),
      ]);
    }),
    getAccountById(id) {
      return self.paypalAccounts.find(acc => acc.id === id) || { name: '' };
    },
    getPayoneerAccountById(id) {
      return self.payoneerAccounts.find(acc => acc.id === id) || { name: '' };
    },
    getStripeAccountById(id) {
      return self.stripeAccounts.find(item => item.cards.find(card => card.id === id));
    },
  }));

export default ConnectionManagement;
